import { labelFromFilename, migrateMapDocument, type MapDocument } from '../../sim/map/index.ts';
import { isSafeMapFilename } from '../../sim/map/filename.ts';

export type MapCatalogEntry = {
  id: string;
  label: string;
  map: MapDocument;
};

export const MAP_CATALOG_PATH = '/__battleboats/maps';

const mapModules = import.meta.glob('/maps/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function idFromPath(path: string): string {
  return path.replace(/^\/maps\//, '').replace(/\.json$/i, '');
}

/** Preview/production catalog from the Vite glob. DEV prefers a live disk listing. */
export function buildCatalog(modules: Record<string, unknown>): MapCatalogEntry[] {
  const entries: MapCatalogEntry[] = [];

  for (const [path, data] of Object.entries(modules)) {
    const map = migrateMapDocument(data);

    if (!map) {
      console.warn(`Skipping ${path}: not a valid Battleboats map document.`);
      continue;
    }

    const id = idFromPath(path);
    entries.push({ id, label: map.name.trim() || labelFromFilename(id), map });
  }

  return entries.sort((a, b) => a.label.localeCompare(b.label));
}

export function catalogFromRepoPayload(value: unknown): MapCatalogEntry[] | null {
  if (!isRecord(value) || !Array.isArray(value.maps)) {
    return null;
  }

  const modules: Record<string, unknown> = {};

  for (const entry of value.maps) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || !isSafeMapFilename(`${entry.id}.json`)) {
      continue;
    }

    modules[`/maps/${entry.id}.json`] = entry.data;
  }

  return buildCatalog(modules);
}

const bundledMapCatalog: MapCatalogEntry[] = buildCatalog(mapModules);

export async function fetchMapCatalogFromRepo(): Promise<MapCatalogEntry[] | null> {
  if (!import.meta.env.DEV) {
    return null;
  }

  try {
    const response = await fetch(MAP_CATALOG_PATH, { cache: 'no-store' });

    if (!response.ok) {
      return null;
    }

    return catalogFromRepoPayload(await response.json());
  } catch {
    return null;
  }
}

/** DEV reads `maps/` from disk. Preview and production use the files bundled at build time. */
export async function loadMapCatalog(): Promise<readonly MapCatalogEntry[]> {
  return (await fetchMapCatalogFromRepo()) ?? bundledMapCatalog;
}

export function findMapCatalogEntry(
  id: string,
  catalog: readonly MapCatalogEntry[],
): MapCatalogEntry | undefined {
  return catalog.find((entry) => entry.id === id);
}
